/**
 * Google Places (New) — Nearby Search along a route, via our edge function.
 *
 * Why edge-function-backed:
 *   The browser cannot safely hold a Google Places API key — it would be
 *   trivially scrapable from Network and could burn the developer's billing.
 *   We sample the route in the browser, ship the points to
 *   `places-along-route` which calls Google server-side, and get back hits.
 *
 * Internal "type" buckets:
 *   - cafe  — coffee shops + bakeries (the "stop for a coffee" feel)
 *   - shop  — strollable shops (book, bakery, gift/antique, art gallery, jewelry, florist)
 *   - park / tourist_attraction / museum / church / library — used by the
 *     pleasantness scorer for green/historic/lively bonuses.
 *   - restaurant — light lively bonus only.
 *
 * This file owns the mapping from Google's many `types[]` strings onto our
 * compact internal type, and the per-type weight used by scoring.
 */
import type { LngLat } from "@/utils/polyline";
import { samplePolylineMax, totalDistance } from "@/utils/polyline";
import { readCache, writeCache } from "@/utils/poiCache";
import { supabase } from "@/integrations/supabase/client";

export type GooglePlaceType =
  | "park"
  | "tourist_attraction"
  | "museum"
  | "church"
  | "library"
  | "cafe"
  | "shop"
  | "restaurant"
  | "gas"
  | "bathroom";

export interface GooglePlaceHit {
  id: string;
  name: string;
  type: GooglePlaceType;
  /** Per-type weight (parks/attractions high, café/shop/restaurant light). */
  weight: number;
  rating: number;
  userRatingCount: number;
  center: LngLat; // [lng, lat]
  /** Optional curator-written blurb shown in the "why visit" popover. */
  blurb?: string;
}

/**
 * Type weights — parks/scenic attractions/landmarks heavily rewarded,
 * generic commercial places (café/shop/restaurant) very lightly. Single
 * source of truth for "what counts as pleasant".
 */
export const PLACE_TYPE_WEIGHTS: Record<GooglePlaceType, number> = {
  park: 1.6,
  tourist_attraction: 1.4,
  museum: 1.2,
  church: 1.0,
  library: 1.0,
  cafe: 0.5,
  shop: 0.4,
  restaurant: 0.25,
  // Gas is a NEED, not a delight — small but non-zero so it survives
  // ranking and shows up as a choosable stop (Morgan) without ever
  // outranking a park or a view.
  gas: 0.35,
  bathroom: 0,
};

/** Internal categories → Google's `includedTypes` strings. */
const TYPE_TO_GOOGLE: Record<GooglePlaceType, string[]> = {
  cafe: ["cafe", "coffee_shop", "bakery"],
  // "Strollable shops" — places people actually browse / window-shop, not
  // big-box stores or supermarkets.
  shop: ["book_store", "gift_shop", "art_gallery", "jewelry_store", "florist", "antique_store"],
  park: ["park"],
  tourist_attraction: ["tourist_attraction"],
  museum: ["museum"],
  church: ["church"],
  library: ["library"],
  restaurant: ["restaurant"],
  gas: ["gas_station"],
  bathroom: ["public_bathroom", "rest_stop"],
};

/** Reverse map: Google's type string → our internal bucket. First match wins. */
const GOOGLE_TO_TYPE: { google: string; type: GooglePlaceType }[] = (
  Object.entries(TYPE_TO_GOOGLE) as [GooglePlaceType, string[]][]
).flatMap(([type, googles]) => googles.map((g) => ({ google: g, type })));

/** Default categories sampled along a route (excludes restaurant — too noisy). */
export const DEFAULT_TYPES: GooglePlaceType[] = [
  "park",
  "tourist_attraction",
  "museum",
  "church",
  "library",
  "cafe",
  "shop",
];

const MIN_RATING = 4.0;
const MIN_REVIEWS = 20;

// 1h cache per (route start, end, types) keeps iterative dev cheap.
const CACHE_TTL_MS = 60 * 60 * 1000;

interface CachedRouteHits {
  ts: number;
  hits: GooglePlaceHit[];
}

interface PlacesAlongRouteResponse {
  hits?: Array<Parameters<typeof mapHit>[0]>;
  error?: string;
}

interface SearchAlongRouteOptions {
  /** Spacing between samples in meters. Default 150m. */
  sampleSpacingM?: number;
  /** Radius per Nearby Search call. Default 250m. */
  radiusM?: number;
  /** Hard cap on number of Nearby Search calls (cost guard). Default 30. */
  maxSamples?: number;
  /** Internal categories to query. Default = DEFAULT_TYPES. */
  types?: GooglePlaceType[];
  /** Minimum rating filter. Default 4.0. */
  minRating?: number;
  /** Minimum review count filter. Default 20. */
  minReviews?: number;
}

/**
 * Sample the route, call our edge function, and return deduped hits tagged
 * with our internal type bucket + scoring weight.
 */
export async function searchPlacesAlongRoute(
  coordinates: LngLat[],
  opts: SearchAlongRouteOptions = {},
): Promise<GooglePlaceHit[]> {
  if (coordinates.length < 2) return [];

  const spacing = opts.sampleSpacingM ?? 150;
  const radius = opts.radiusM ?? 250;
  const maxSamples = opts.maxSamples ?? 30;
  const types = opts.types ?? DEFAULT_TYPES;
  const minRating = opts.minRating ?? MIN_RATING;
  const minReviews = opts.minReviews ?? MIN_REVIEWS;

  // Sample by spacing, but cap to keep cost bounded on long road trips.
  const ideal = Math.max(2, Math.ceil(totalDistance(coordinates) / spacing));
  const sampleCount = Math.min(ideal, maxSamples);
  const samples = samplePolylineMax(coordinates, sampleCount);

  // Cache key uses route endpoints + sample count + types + thresholds.
  const start = coordinates[0];
  const end = coordinates[coordinates.length - 1];
  const r = (n: number) => Math.round(n * 1000) / 1000;
  const cacheK = `gpl:v3:${r(start[0])},${r(start[1])}->${r(end[0])},${r(end[1])}:${sampleCount}:${radius}:${types.join(",")}:${minRating}:${minReviews}`;
  const cached = readCache<CachedRouteHits>(cacheK);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) return cached.hits;

  // OSM PROVIDER (Morgan, Aug 4: "I don't even use ratings"). The edge
  // function answers from OpenStreetMap — free, no Google — and expects our
  // INTERNAL type names. rating/userRatingCount come back 0 and are no
  // longer displayed or scored anywhere.
  let hits: GooglePlaceHit[] = [];
  const { data, error } = await supabase.functions.invoke<PlacesAlongRouteResponse>("places-along-route", {
    body: {
      provider: "osm",
      samples,
      radiusM: radius,
      types,
      minRating,
      minReviews,
    },
  });
  if (error) throw new Error(error.message ?? "places-along-route failed");
  if (!data) throw new Error("places-along-route returned no data");
  if (typeof data.error === "string") throw new Error(data.error);
  const raw = Array.isArray(data.hits) ? data.hits : [];
  hits = raw
    .map((h) => mapHit(h, types))
    .filter((h): h is GooglePlaceHit => h !== null);

  if (hits.length > 0) writeCache<CachedRouteHits>(cacheK, { ts: Date.now(), hits });
  return hits;
}

/** Pick the best internal bucket for a raw place by scanning its types[]. */
function mapHit(
  raw: { id?: string; name?: string; lng?: number; lat?: number; rating?: number; userRatingCount?: number; types?: string[] },
  requested: GooglePlaceType[],
): GooglePlaceHit | null {
  if (!raw || typeof raw.id !== "string") return null;
  if (typeof raw.lng !== "number" || typeof raw.lat !== "number") return null;
  const ptypes = Array.isArray(raw.types) ? raw.types : [];
  // Walk our priority list — first match against the place's types wins.
  // We skip buckets the caller didn't request so a "cafe-only" call doesn't
  // accidentally surface a museum that happened to also be tagged "cafe".
  const requestedSet = new Set(requested);
  let matched: GooglePlaceType | null = null;
  // OSM-provider hits (Aug 4) carry our INTERNAL type name directly in
  // types[] — accept it before falling back to the Google-name reverse map.
  for (const t of ptypes) {
    if (requestedSet.has(t as GooglePlaceType)) { matched = t as GooglePlaceType; break; }
  }
  if (!matched) {
    for (const { google, type } of GOOGLE_TO_TYPE) {
      if (!requestedSet.has(type)) continue;
      if (ptypes.includes(google)) { matched = type; break; }
    }
  }
  if (!matched) return null;
  return {
    id: raw.id,
    name: typeof raw.name === "string" ? raw.name : "Unnamed",
    type: matched,
    weight: PLACE_TYPE_WEIGHTS[matched],
    rating: typeof raw.rating === "number" ? raw.rating : 0,
    userRatingCount: typeof raw.userRatingCount === "number" ? raw.userRatingCount : 0,
    center: [raw.lng, raw.lat],
  };
}
